// src/pages/Tiffin.jsx
// Tiffin page displayed like Menu.jsx with menu sections/cards.
// Customer reviews the 4-week menu and clicks Order Tiffin to complete ordering/payment on DineSpot.

import React from "react";
import templeOverlay from "../assets/temple-overlay.png";
import MobileNavbar from "../components/MobileNavbar";

/* ================= THEME — SAME STYLE AS MENU PAGE ================= */
const theme = {
  bg: "#FFF9F0",
  card: "#FFFFFF",
  ink: "#12262A",
  sub: "#3C4A4E",
  divider: "#E8DDC8",
  accent: "#C69A35",
  wash: "#F6EAD0",
  orange: "#C86B1E",
};

const fonts = {
  heading:
    '"Playfair Display", ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
  body:
    'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial',
};

// Replace this with your final DineSpot tiffin ordering URL
const DINESPOT_TIFFIN_URL = "https://carnaticcafe.dinespot.io";

/* ================= UI COMPONENTS ================= */
const OrderButton = ({ fullWidth = false }) => (
  <a
    href={DINESPOT_TIFFIN_URL}
    target="_blank"
    rel="noopener noreferrer"
    style={{
      display: "inline-flex",
      justifyContent: "center",
      alignItems: "center",
      width: fullWidth ? "100%" : "auto",
      background: theme.orange,
      color: "#FFFFFF",
      borderRadius: 999,
      padding: "13px 28px",
      fontFamily: fonts.body,
      fontWeight: 800,
      fontSize: 16,
      textDecoration: "none",
      boxShadow: "0 8px 20px rgba(0,0,0,0.16)",
    }}
  >
    🍱 Order Tiffin
  </a>
);

const Item = ({ name }) => (
  <li
    style={{
      background: theme.card,
      border: `1px solid ${theme.divider}`,
      borderRadius: 14,
      padding: 14,
      boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
      fontFamily: fonts.body,
      color: theme.ink,
      fontSize: 17,
      fontWeight: 700,
      lineHeight: 1.4,
    }}
  >
    {name}
  </li>
);

const CategoryColumn = ({ title, items }) => (
  <div>
    <h4
      style={{
        margin: "0 0 12px",
        fontFamily: fonts.heading,
        fontSize: 21,
        fontWeight: 800,
        color: theme.ink,
        borderBottom: `2px solid ${theme.accent}`,
        display: "inline-block",
        paddingBottom: 4,
        textTransform: "uppercase",
      }}
    >
      {title}
    </h4>

    <ul
      style={{
        listStyle: "none",
        padding: 0,
        margin: 0,
        display: "grid",
        gap: 10,
      }}
    >
      {items.map((item, index) => (
        <Item key={index} name={item} />
      ))}
    </ul>
  </div>
);

const WeekSection = ({ week, starters, rice, curry, sides }) => (
  <section style={{ marginBottom: 38 }}>
    <h3
      style={{
        fontFamily: fonts.heading,
        fontSize: 30,
        fontWeight: 900,
        color: theme.ink,
        borderBottom: `2px solid ${theme.accent}`,
        display: "inline-block",
        paddingBottom: 5,
        marginBottom: 18,
        textTransform: "uppercase",
      }}
    >
      {week}
    </h3>

    <div
      style={{
        background: "rgba(255,255,255,0.86)",
        border: `1px solid ${theme.divider}`,
        borderRadius: 18,
        padding: 18,
        boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 18,
        }}
      >
        <CategoryColumn title="Starters" items={starters} />
        <CategoryColumn title="Rice" items={rice} />
        <CategoryColumn title="Curries / Mains" items={curry} />
        <CategoryColumn title="Sides" items={sides} />
      </div>
    </div>
  </section>
);

/* ================= TIFFIN DATA FROM PROVIDED MENU ================= */
const tiffinWeeks = [
  {
    week: "Week 1",
    starters: ["Vada", "Chilli Bajji", "Masala Vada", "Potato Bajji", "Mysore Bonda"],
    rice: ["Veg Biryani", "Jeera Rice", "Curd Rice", "Plain Rice", "Bisi Bele Bath"],
    curry: ["Veg Kurma", "Rajma", "Potato Fry", "Egg Plant Curry", "Raw Banana Fry"],
    sides: ["Raita", "Salad", "Pickle", "Papad", "Pickle"],
  },
  {
    week: "Week 2",
    starters: ["Dahi Bhalla", "Chilli Idli", "Masala Vada", "Mix Pakoda", "Sambar Vada"],
    rice: ["Mushroom Biryani", "Tomato Rice", "Pongal", "Plain Rice", "Sambar Rice"],
    curry: ["Gobi 65 (Semi-Gravy)", "Cabbage Poriyal", "Sambar", "Dal", "Beetroot Poriyal"],
    sides: ["Raita", "Sweet", "Rasam", "Sweet", "Salad"],
  },
  {
    week: "Week 3",
    starters: ["Onion Pakora", "Chilli Bajji", "Vada", "Podi Idli", "Mysore Bonda"],
    rice: ["Paneer Biryani", "Lemon Rice", "Plain Rice", "Fried Rice", "Tamarind Rice"],
    curry: ["Raita & Salad", "Potato Fry", "Channa Masala", "Gobi Manchurian", "Mix Veg"],
    sides: ["Sweet", "Pickle", "Rasam", "Sweet", "Curd"],
  },
  {
    week: "Week 4",
    starters: ["Onion Bajji", "Aloo Bonda", "Dal Vada", "Mix Pakora", "Sambar Vada"],
    rice: ["Soya Chunk Biryani", "Matar Rice", "Plain Rice", "Veg Pulav", "Idli (3 Piece)"],
    curry: ["Kurma", "Paneer Butter Masala", "Mushroom Masala", "Rajma", "Sambar"],
    sides: ["Raita", "Sweet", "Rasam", "Salad", "Upma (8 oz)"],
  },
];

export default function Tiffin() {
  return (
    <div className="relative min-h-screen">
      <MobileNavbar />

      {/* BACKGROUND IMAGE — SAME PATTERN AS MENU/CATERING */}
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: theme.bg,
          backgroundImage: `url(${templeOverlay})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          opacity: 0.18,
          mixBlendMode: "overlay",
          zIndex: 0,
        }}
      />

      <main
        className="relative z-10 pt-20 md:pt-8 px-5 pb-28"
        style={{
          maxWidth: 1080,
          margin: "0 auto",
          fontFamily: fonts.body,
          color: theme.ink,
        }}
      >
        {/* ORANGE BANNER — SAME FEEL AS CATERING */}
        <div
          style={{
            padding: 14,
            background: theme.orange,
            border: "1px solid #A65114",
            borderRadius: 8,
            marginBottom: 24,
            color: "white",
            fontFamily: fonts.body,
            fontSize: 14,
            lineHeight: "1.5",
          }}
        >
          <p>* Tiffin menu rotates every 4 weeks.</p>
          <p>* Final ordering, item selection, payment, pickup and delivery details are completed on DineSpot.</p>
          <p>* Menu items may vary slightly based on seasonal availability.</p>
        </div>

        {/* TITLE */}
        <section style={{ textAlign: "center", marginBottom: 34 }}>
          <h2
            style={{
              textAlign: "center",
              fontFamily: fonts.heading,
              fontSize: 40,
              fontWeight: "bold",
              marginBottom: 12,
              color: theme.ink,
            }}
          >
            Tiffin Menu
          </h2>

          <p
            style={{
              maxWidth: 760,
              margin: "0 auto 20px",
              color: theme.sub,
              lineHeight: 1.6,
              fontSize: 16,
            }}
          >
            Fresh pure vegetarian home-style meals from Carnatic Cafe. Browse the 4-week rotating tiffin menu below and click Order Tiffin to complete your order on DineSpot.
          </p>

          <OrderButton />
        </section>

        {/* WEEKLY MENU DISPLAY — SAME CARD STYLE AS MENU PAGE */}
        {tiffinWeeks.map((week) => (
          <WeekSection key={week.week} {...week} />
        ))}

        {/* BOTTOM NOTE */}
        <section
          style={{
            background: "rgba(255,255,255,0.86)",
            border: `1px solid ${theme.divider}`,
            borderRadius: 16,
            padding: 18,
            marginTop: 10,
            color: theme.sub,
            lineHeight: 1.6,
          }}
        >
          <strong style={{ color: theme.ink }}>How to order:</strong> Click the Order Tiffin button. You will be redirected to the DineSpot portal where item selection, customer details, checkout and payment will be handled.
        </section>
      </main>

      {/* STICKY ORDER BUTTON */}
      <div
        className="fixed bottom-5 left-0 right-0 z-40 flex justify-center px-4"
        style={{ pointerEvents: "none" }}
      >
        <div style={{ pointerEvents: "auto" }}>
          <OrderButton />
        </div>
      </div>
    </div>
  );
}
